import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Bookmark, Trash2, BookOpen, Users, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const itemTypeIcons = {
  story: FileText,
  person: Users,
  media: BookOpen
};

export default function BookmarksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchBookmarks = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/bookmarks`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        setBookmarks(res.data);
      } catch (error) {
        console.error('Error fetching bookmarks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookmarks();
  }, [user, navigate]);

  const handleDelete = async (bookmarkId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bookmarks/${bookmarkId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Error removing bookmark');
    }
  };

  const getItemLink = (bookmark) => {
    switch (bookmark.item_type) {
      case 'story': return `/story/${bookmark.item_id}`;
      case 'person': return `/people/${bookmark.item_id}`;
      case 'media': return `/media/${bookmark.item_id}`;
      default: return '/';
    }
  };

  const formatItemId = (itemId) => {
    return itemId
      .replace(/^(story_|person_|media_)/, '')
      .replace(/_/g, ' ')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Bookmark className="w-8 h-8 text-cyan-400" />
            <h1 className="font-heading text-3xl md:text-5xl font-bold" data-testid="bookmarks-title">
              Your Bookmarks
            </h1>
          </div>
          <p className="text-white/50">
            Save stories, people, and media to come back to later.
          </p>
        </div>

        {/* Bookmarks List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-sm" />
            ))}
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-20 card-dark">
            <Bookmark className="w-12 h-12 mx-auto mb-4 text-white/20" />
            <p className="text-white/50 mb-6">No bookmarks yet. Start exploring and save items you want to revisit.</p>
            <Link to="/">
              <Button className="btn-outline">Browse Stories</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookmarks-list">
            {bookmarks.map(bookmark => {
              const Icon = itemTypeIcons[bookmark.item_type] || FileText;
              return (
                <div key={bookmark.bookmark_id} className="card-dark p-4 flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs uppercase tracking-wider text-white/40 mb-1 block">
                      {bookmark.item_type}
                    </span>
                    <p className="text-white font-medium truncate">
                      {formatItemId(bookmark.item_id)}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Link to={getItemLink(bookmark)}>
                      <Button variant="ghost" size="sm" className="text-white/60 hover:text-cyan-400">
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(bookmark.bookmark_id)}
                      className="text-white/60 hover:text-red-400"
                      data-testid={`delete-${bookmark.bookmark_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
